import { useLayoutEffect, useMemo, useRef, useState, useEffect, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthProvider";
import Notifications from "../notification/Notifications";
import ProfileMenue from "../users/userProfile/ProfileMenu";
import "./Header.css";
import BaknayLogo from "../../common/brand/BaknayLogo";
import QuickActionsLauncher from "../quickActions/QuickActionsLauncher";
import { useIsAdmin } from "../hooks/useIsAdmin";

const getInitials = (u) => {
  const a = (u?.firstName || "").trim();
  const b = (u?.lastName || "").trim();
  return ((a[0] || "") + (b[0] || "")).toUpperCase() || "U";
};


export default function Header({ menuOpen, setMenuOpen, basePath, scrollContainer }) {
  const { isAuthenticated, user, hasRole } = useAuth();
  const isBusinessAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const headerRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const initials = useMemo(() => getInitials(user), [user]);
  const menuButtonId = useId();
  const setHeaderOffset = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const node = headerRef.current;
    if (!node) return;
    const height = Math.round(node.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--shell-header-offset", `${height}px`);
  }, []);

  const updatePosition = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuW = menuRef.current?.offsetWidth ?? 280;
    const gap = 8;
    const vw = window.innerWidth;
    let left = Math.round(r.right - menuW);
    left = Math.max(8, Math.min(left, vw - menuW - 8));
    const top = Math.round(r.bottom + gap);
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const onResizeScroll = () => updatePosition();
    window.addEventListener("resize", onResizeScroll);
    window.addEventListener("scroll", onResizeScroll, true);
    return () => {
      window.removeEventListener("resize", onResizeScroll);
      window.removeEventListener("scroll", onResizeScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const target = scrollContainer ?? window;
    if (!target) return;

    const handleScroll = () => {
      const offset = target === window ? window.scrollY : target.scrollTop;
      setScrolled(offset > 2);
    };

    handleScroll();
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      target.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainer]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    setHeaderOffset();
    window.addEventListener("resize", setHeaderOffset);
    return () => {
      window.removeEventListener("resize", setHeaderOffset);
      if (typeof document !== "undefined") {
        document.documentElement.style.removeProperty("--shell-header-offset");
      }
    };
  }, [setHeaderOffset]);

  useLayoutEffect(() => {
    setHeaderOffset();
  }, [setHeaderOffset, menuOpen, isAuthenticated]);

  const brandHref = basePath || "/home";
  const headerClassName = [
    "header",
    "sticky-top",
    "py-3",
    "px-4",
    "d-flex",
    "align-items-center",
    scrolled ? "header--scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isGlobalAdminShell = basePath === "/admin";
  const canUseQuickActions =
    isAuthenticated && !isGlobalAdminShell && !(hasRole?.("AGENT") && !isBusinessAdmin);

  return (
    <header ref={headerRef} className={headerClassName}>
      <div className="order-1 order-lg-2 d-lg-flex align-items-center ms-0 me-3 me-lg-0">
        {isAuthenticated && (
          <button
            className="menu-btn d-lg-none"
            onClick={() => setMenuOpen(!menuOpen)}
            onMouseDown={(e) => e.preventDefault()}
            aria-label="Toggle menu"
          >
            {menuOpen ? "✕" : "☰"}
          </button>
        )}
      </div>

      <div className="order-2 order-lg-1 flex-grow-1 d-flex justify-content-end justify-content-lg-start">
        <Link
          to={brandHref}
          className="header-brand d-none d-lg-inline-flex"
          aria-label="Baknay home"
        >
          <BaknayLogo size="sm" />
        </Link>
      </div>

      {isAuthenticated && (
        <div className="order-3 d-flex align-items-center ms-3 gap-3">
          {canUseQuickActions && (
            <QuickActionsLauncher
              buttonClassName="d-inline-flex align-items-center gap-1"
              showLabel
              variant="header"
              enableHotkey
            />
          )}
          <Notifications limit={20} pollMs={15000} />
          <button
            ref={btnRef}
            id={menuButtonId}
            className="avatar-btn d-flex align-items-center gap-2"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? `${menuButtonId}-menu` : undefined}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="avatar-circle" aria-hidden="true">{initials}</span>
            <span className="visually-hidden">
              {open ? "Close profile menu" : "Open profile menu"}
            </span>
          </button>

          {open &&
            createPortal(
              <div
                className="profile-dropdown-portal"
                style={{ top: pos.top, left: pos.left }}
                role="presentation"
              >
                <div
                  ref={menuRef}
                  id={`${menuButtonId}-menu`}
                  role="menu"
                  aria-labelledby={menuButtonId}
                  tabIndex={-1}
                >
                  <ProfileMenue
                    profileMenuOpen={open}
                    setProfileMenuOpen={setOpen}
                    basePath={basePath}
                  />
                </div>
              </div>,
              document.body
            )}
        </div>
      )}
    </header>
  );
}
